import React from 'react';
import styled from '@emotion/styled';

interface FormMultiCheckBoxProps {
    options: string[][];
    value: (string | undefined)[];
    onChange: (value: string[]) => void;
    placeholder?: string;
    idPrefix?: string;
}

const Container = styled.div`
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    background-color: #f8fafc;
    padding: 16px 20px;
`;

const SelectAllContainer = styled.div`
    margin-bottom: 12px;
    padding-bottom: 8px;
    border-bottom: 1px solid #e0e0e0;
`;

const SelectAllCheckbox = styled.div`
    display: flex;
    align-items: center;
    font-weight: 700;
    color: var(--dark-blue);
    font-size: var(--font-large);
`;

const CheckboxGrid = styled.div`
    display: flex;
    flex-direction: column;
`;

const CheckboxItem = styled.div`
    display: flex;
    align-items: flex-start;
    font-size: var(--font-medium);
    padding: 4px 0;
    transition: background 0.2s;
    border-radius: 4px;
    min-height: 20px;
    &:hover {
        background: #e6f0fa;
    }
`;

const Checkbox = styled.input`
    margin-right: 8px;
    margin-top: 2px;
    cursor: pointer;
    appearance: auto;
    flex-shrink: 0;
`;

const Label = styled.label`
    cursor: pointer;
    user-select: none;
    font-size: var(--font-medium);
    line-height: 1.2;
    text-align: left;
    flex: 1;
`;

export const FormMultiCheckBox = React.forwardRef<HTMLDivElement, FormMultiCheckBoxProps>(
    ({ options, value, onChange, idPrefix = 'checkbox' }, ref) => {
        const filteredValue = (value || []).filter((item): item is string => item !== undefined);

        const handleCheckboxChange = (optionValue: string, checked: boolean) => {
            let newValue: string[];

            if (checked) {
                newValue = [...filteredValue, optionValue];
            } else {
                newValue = filteredValue.filter((code) => code !== optionValue);
            }

            onChange(newValue);
        };

        const handleSelectAllClick = () => {
            const allValues = options.map((option) => option[0]);
            const selectedCount = allValues.filter((value) => filteredValue.includes(value)).length;

            if (selectedCount === allValues.length) {
                // 全て選択されている場合は全て解除
                onChange([]);
            } else {
                // それ以外の場合は全て選択
                onChange(allValues);
            }
        };

        const isAllSelected = () => {
            const allValues = options.map((option) => option[0]);
            return allValues.every((value) => filteredValue.includes(value));
        };

        return (
            <Container ref={ref}>
                <SelectAllContainer>
                    <SelectAllCheckbox>
                        <Checkbox
                            id={`${idPrefix}-select-all-checkbox`}
                            type="checkbox"
                            checked={isAllSelected()}
                            onChange={() => handleSelectAllClick()}
                        />
                        <Label htmlFor={`${idPrefix}-select-all-checkbox`}>全て選択</Label>
                    </SelectAllCheckbox>
                </SelectAllContainer>
                <CheckboxGrid>
                    {options.map((option, optionIdx) => {
                        const optionValue = option[0];
                        const optionLabel = option[1];
                        const checkboxId = `${idPrefix}-checkbox-${optionIdx}`;

                        return (
                            <CheckboxItem key={optionValue}>
                                <Checkbox
                                    id={checkboxId}
                                    type="checkbox"
                                    checked={filteredValue.includes(optionValue)}
                                    onChange={(e) => handleCheckboxChange(optionValue, e.target.checked)}
                                />
                                <Label htmlFor={checkboxId}>{optionLabel}</Label>
                            </CheckboxItem>
                        );
                    })}
                </CheckboxGrid>
            </Container>
        );
    },
);
